import React, { useState, useRef, useEffect } from 'react';
import { getAccessToken } from './authSession';
import JsBarcode from 'jsbarcode';
import HamburgerMenu from './HamburgerMenu';
import { resolveApiBaseURL } from './api';

const API_BASE_URL = `${resolveApiBaseURL()}/tracked-parts`;
const SITES_URL = `${resolveApiBaseURL()}/sites/`;

// Safe, pure-JS barcode generator that prevents React version conflicts
const BarcodeDisplay = ({ value }) => {
  const svgRef = useRef(null);

  useEffect(() => {
    if (svgRef.current && value) {
      JsBarcode(svgRef.current, value, {
        width: 2,
        height: 60,
        fontSize: 16,
        displayValue: true,
      });
    }
  }, [value]);

  return <svg ref={svgRef}></svg>;
};

const PartTracker = ({ theme }) => {
  const [isDarkMode, setIsDarkMode] = useState(theme === 'dark');
  const [activeTab, setActiveTab] = useState('assign');

  const [sites, setSites] = useState([]);
  const [formData, setFormData] = useState({
    site: '',
    nomenclature: '',
    nsn: '',
    serial_number: '',
    tail_number: '',
    location: 'Location 1', // New default location
    status: 'AWAITING_MAINTENANCE'
  });
  const [generatedPart, setGeneratedPart] = useState(null);

  const [scanInput, setScanInput] = useState('');
  const [scannedPart, setScannedPart] = useState(null);

  const [inventoryList, setInventoryList] = useState([]);

  // State to track which locations are expanded (defaults to empty/collapsed)
  const [expandedLocations, setExpandedLocations] = useState({});

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const scanInputRef = useRef(null);

  useEffect(() => {
    setIsDarkMode(theme === 'dark');
  }, [theme]);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      return;
    }
    fetch(SITES_URL, { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => (response.ok ? response.json() : []))
      .then((data) => {
        const rows = Array.isArray(data) ? data : (data.results || []);
        setSites(rows);
        if (rows.length === 1) {
          setFormData((current) => ({ ...current, site: String(rows[0].id) }));
        }
      })
      .catch(() => setSites([]));
  }, []);

  useEffect(() => {
    if (activeTab === 'scan' && scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [activeTab]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    const token = getAccessToken();

    try {
      const response = await fetch(`${API_BASE_URL}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || JSON.stringify(errorData) || 'Failed to assign part.';
        throw new Error(`Server said: ${errorMessage}`);
      }

      const data = await response.json();
      setGeneratedPart(data);
      setMessage({ type: 'success', text: 'Part assigned successfully!' });

      // Reset form but keep defaults
      setFormData((current) => ({
        site: current.site,
        nomenclature: '',
        nsn: '',
        serial_number: '',
        tail_number: '',
        location: 'Location 1',
        status: 'AWAITING_MAINTENANCE'
      }));
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleScanSubmit = async (e) => {
    e.preventDefault();
    if (!scanInput) return;

    setLoading(true);
    setMessage({ type: '', text: '' });
    setScannedPart(null);

    const token = getAccessToken();

    try {
      const response = await fetch(`${API_BASE_URL}/scan/?barcode_id=${scanInput}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Barcode not found in active inventory.');
        }
        throw new Error('Failed to fetch part data.');
      }

      const data = await response.json();
      setScannedPart(data);
      setScanInput('');
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
      setScanInput('');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!scannedPart) return;

    const token = getAccessToken();

    try {
      const response = await fetch(`${API_BASE_URL}/${scannedPart.id}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error('Failed to update status.');
      }

      const data = await response.json();
      setScannedPart(data);
      setMessage({ type: 'success', text: 'Status updated successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const fetchInventory = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    const token = getAccessToken();

    try {
      const response = await fetch(`${API_BASE_URL}/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch inventory.');

      const data = await response.json();
      console.log("RAW DJANGO DATA:", data);
      setInventoryList(Array.isArray(data) ? data : (data.results || []));
      // Ensure all sections are collapsed when fresh data is fetched
      setExpandedLocations({});
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleInventoryStatusChange = async (partId, newStatus) => {
    const token = getAccessToken();

    try {
      const response = await fetch(`${API_BASE_URL}/${partId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error('Failed to update status.');
      }

      const updatedPart = await response.json();

      setInventoryList(prevList => prevList.map(part => part.id === partId ? updatedPart : part));
      setMessage({ type: 'success', text: 'Status updated successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleDeletePart = async (partId) => {
    const isConfirmed = window.confirm("Are you sure you want to delete this part? This will remove it from active inventory.");

    if (!isConfirmed) return;

    setLoading(true);
    const token = getAccessToken();

    try {
      const response = await fetch(`${API_BASE_URL}/${partId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_deleted: true })
      });

      if (!response.ok) {
        throw new Error('Failed to delete part.');
      }

      setInventoryList(prevList => prevList.filter(part => part.id !== partId));

      if (generatedPart && generatedPart.id === partId) {
        setGeneratedPart(null);
      }

      setMessage({ type: 'success', text: 'Part successfully deleted.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Group inventory list by Location instead of Tail Number
  const groupedInventory = inventoryList.reduce((acc, part) => {
    const loc = part.location || 'Unassigned';
    if (!acc[loc]) {
      acc[loc] = [];
    }
    acc[loc].push(part);
    return acc;
  }, {});

  const toggleLocationCollapse = (locationName) => {
    setExpandedLocations(prev => ({
      ...prev,
      [locationName]: !prev[locationName]
    }));
  };

  const currentTheme = isDarkMode ? 'dark' : 'light';

  const getTabStyle = (isActive) => {
    return {
      flex: 1,
      padding: '12px',
      fontSize: '1.1rem',
      fontWeight: 'bold',
      cursor: 'pointer',
      border: 'none',
      borderRadius: '6px',
      backgroundColor: isActive ? '#007bff' : (isDarkMode ? '#333' : '#e0e0e0'),
      color: isActive ? '#fff' : (isDarkMode ? '#fff' : '#000')
    };
  };

  return (
    <div style={{ backgroundColor: isDarkMode ? '#121212' : '#ffffff', color: isDarkMode ? '#ffffff' : '#000000', minHeight: '100vh', transition: 'all 0.3s ease' }}>
      <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>

        <div className="no-print" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', borderBottom: isDarkMode ? '1px solid #333' : '1px solid #eee', paddingBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <HamburgerMenu theme={currentTheme}/>
            <h1 style={{ margin: 0, fontSize: '1.8rem' }}>Canned Part Tracker</h1>
          </div>

          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            style={{ padding: '8px 12px', backgroundColor: isDarkMode ? '#444' : '#ddd', color: isDarkMode ? '#fff' : '#000', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
          >
            {isDarkMode ? '☀️ Light Mode' : '🌙 Dark Mode'}
          </button>
        </div>

        {message.text && (
          <div className="no-print" style={{ padding: '10px', marginBottom: '20px', borderRadius: '4px', backgroundColor: message.type === 'error' ? '#f8d7da' : '#d4edda', color: message.type === 'error' ? '#721c24' : '#155724' }}>
            {message.text}
          </div>
        )}

        <div className="no-print" style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button
            style={getTabStyle(activeTab === 'assign')}
            onClick={() => { setActiveTab('assign'); setGeneratedPart(null); setMessage({type:'', text:''}); }}
          >
            Receive Canned Part
          </button>
          <button
            style={getTabStyle(activeTab === 'scan')}
            onClick={() => { setActiveTab('scan'); setScannedPart(null); setMessage({type:'', text:''}); }}
          >
            Scan Part
          </button>
          <button
            style={getTabStyle(activeTab === 'inventory')}
            onClick={() => { setActiveTab('inventory'); setGeneratedPart(null); setMessage({type:'', text:''}); fetchInventory(); }}
          >
            Canned Inventory
          </button>
        </div>

        {/* ASSIGN TAB */}
        {activeTab === 'assign' && (
          <div className="no-print" style={{ backgroundColor: isDarkMode ? '#1e1e1e' : '#f9f9f9', border: `1px solid ${isDarkMode ? '#333' : '#eee'}`, borderRadius: '8px', padding: '20px' }}>

            <form onSubmit={handleAssignSubmit}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Site *</label>
              <select
                name="site"
                required
                value={formData.site}
                onChange={handleInputChange}
                style={{ width: '100%', padding: '10px', marginBottom: '15px', borderRadius: '4px', border: `1px solid ${isDarkMode ? '#555' : '#ccc'}`, backgroundColor: isDarkMode ? '#2c2c2c' : '#fff', color: isDarkMode ? '#fff' : '#000', boxSizing: 'border-box' }}
              >
                <option value="">Select a site</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>{site.name}</option>
                ))}
              </select>

              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Nomenclature *</label>
              <input style={{ width: '100%', padding: '10px', marginBottom: '15px', borderRadius: '4px', border: `1px solid ${isDarkMode ? '#555' : '#ccc'}`, backgroundColor: isDarkMode ? '#2c2c2c' : '#fff', color: isDarkMode ? '#fff' : '#000', boxSizing: 'border-box' }} required type="text" name="nomenclature" value={formData.nomenclature} onChange={handleInputChange} placeholder="e.g. Main Rotor Blade" />

              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Aircraft Tail Number *</label>
              <input style={{ width: '100%', padding: '10px', marginBottom: '15px', borderRadius: '4px', border: `1px solid ${isDarkMode ? '#555' : '#ccc'}`, backgroundColor: isDarkMode ? '#2c2c2c' : '#fff', color: isDarkMode ? '#fff' : '#000', boxSizing: 'border-box' }} required type="text" name="tail_number" value={formData.tail_number} onChange={handleInputChange} placeholder="e.g. 160010" />

              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Storage Location</label>
              <select
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                style={{ width: '100%', padding: '10px', marginBottom: '15px', borderRadius: '4px', border: `1px solid ${isDarkMode ? '#555' : '#ccc'}`, backgroundColor: isDarkMode ? '#2c2c2c' : '#fff', color: isDarkMode ? '#fff' : '#000', boxSizing: 'border-box' }}
              >
                <option value="Location 1">Location 1</option>
                <option value="Location 2">Location 2</option>
                <option value="Location 3">Location 3</option>
                <option value="Location 4">Location 4</option>
                <option value="Location 5">Location 5</option>
              </select>

              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>NSN / Part Number</label>
              <input style={{ width: '100%', padding: '10px', marginBottom: '15px', borderRadius: '4px', border: `1px solid ${isDarkMode ? '#555' : '#ccc'}`, backgroundColor: isDarkMode ? '#2c2c2c' : '#fff', color: isDarkMode ? '#fff' : '#000', boxSizing: 'border-box' }} type="text" name="nsn" value={formData.nsn} onChange={handleInputChange} />

              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Serial Number</label>
              <input style={{ width: '100%', padding: '10px', marginBottom: '15px', borderRadius: '4px', border: `1px solid ${isDarkMode ? '#555' : '#ccc'}`, backgroundColor: isDarkMode ? '#2c2c2c' : '#fff', color: isDarkMode ? '#fff' : '#000', boxSizing: 'border-box' }} type="text" name="serial_number" value={formData.serial_number} onChange={handleInputChange} />

              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Initial Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                style={{ width: '100%', padding: '10px', marginBottom: '15px', borderRadius: '4px', border: `1px solid ${isDarkMode ? '#555' : '#ccc'}`, backgroundColor: isDarkMode ? '#2c2c2c' : '#fff', color: isDarkMode ? '#fff' : '#000', boxSizing: 'border-box' }}
              >
                <option value="AWAITING_MAINTENANCE">Awaiting Maintenance</option>
                <option value="IN_WORK">In Work</option>
                <option value="SENT_TO_SUPPLY">Sent to Supply</option>
                <option value="READY_FOR_ISSUE">Ready for Issue</option>
                <option value="SCRAPPED">Scrapped</option>
              </select>

              <button type="submit" style={{ padding: '10px 20px', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', width: '100%' }} disabled={loading}>
                {loading ? 'Processing...' : 'Assign & Generate Barcode'}
              </button>
            </form>
          </div>
        )}

        {/* SCAN TAB */}
        {activeTab === 'scan' && (
          <div className="no-print" style={{ backgroundColor: isDarkMode ? '#1e1e1e' : '#f9f9f9', border: `1px solid ${isDarkMode ? '#333' : '#eee'}`, borderRadius: '8px', padding: '20px' }}>
            <h2>Scan Barcode</h2>
            <p style={{ color: isDarkMode ? '#aaa' : '#666', marginBottom: '15px' }}>
              Click the box below and pull the scanner trigger.
            </p>

            <form onSubmit={handleScanSubmit}>
              <input
                ref={scanInputRef}
                style={{ width: '100%', padding: '10px', marginBottom: '15px', borderRadius: '4px', border: `1px solid ${isDarkMode ? '#555' : '#ccc'}`, backgroundColor: isDarkMode ? '#2c2c2c' : '#fff', color: isDarkMode ? '#fff' : '#000', boxSizing: 'border-box', fontSize: '1.5rem', textAlign: 'center', letterSpacing: '2px' }}
                type="text"
                value={scanInput}
                onChange={(e) => setScanInput(e.target.value)}
                placeholder="||||||||||||||||||"
                autoFocus
              />
            </form>

            {loading && <p>Searching database...</p>}

            {scannedPart && (
              <div style={{ marginTop: '20px', padding: '15px', border: `1px solid ${isDarkMode ? '#444' : '#ddd'}`, borderRadius: '6px', backgroundColor: isDarkMode ? '#252525' : '#fff' }}>
                <h3 style={{ borderBottom: `1px solid ${isDarkMode ? '#444' : '#eee'}`, paddingBottom: '10px' }}>{scannedPart.nomenclature}</h3>
                <p><strong>Barcode ID:</strong> {scannedPart.barcode_id}</p>
                <p><strong>Tail Number:</strong> {scannedPart.tail_number}</p>
                <p><strong>Location:</strong> {scannedPart.location || 'Unassigned'}</p>
                <p><strong>Current Status:</strong> <span style={{ padding: '4px 8px', backgroundColor: '#ffc107', color: '#000', borderRadius: '4px', fontWeight: 'bold' }}>{scannedPart.status.replace(/_/g, ' ')}</span></p>

                <div style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button onClick={() => handleStatusUpdate('IN_WORK')} style={{ padding: '10px 20px', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', backgroundColor: '#17a2b8' }}>Move to In Work</button>
                  <button onClick={() => handleStatusUpdate('SENT_TO_SUPPLY')} style={{ padding: '10px 20px', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', backgroundColor: '#6c757d' }}>Send to Supply</button>
                  <button onClick={() => handleStatusUpdate('READY_FOR_ISSUE')} style={{ padding: '10px 20px', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', backgroundColor: '#28a745' }}>Mark RFI</button>
                  <button onClick={() => handleStatusUpdate('SCRAPPED')} style={{ padding: '10px 20px', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', backgroundColor: '#dc3545' }}>Scrap Part</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* INVENTORY TAB */}
        {activeTab === 'inventory' && (
          <div className="no-print" style={{ backgroundColor: isDarkMode ? '#1e1e1e' : '#f9f9f9', border: `1px solid ${isDarkMode ? '#333' : '#eee'}`, borderRadius: '8px', padding: '20px', overflowX: 'auto' }}>
            <h2>Canned Inventory</h2>

            {loading ? <p>Loading parts...</p> : (
              <>
                {inventoryList.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#888', padding: '20px' }}>No active parts found in inventory.</p>
                ) : (
                  Object.entries(groupedInventory)
                    .sort(([locA], [locB]) => locA.localeCompare(locB))
                    .map(([locationName, parts]) => {
                      const isExpanded = !!expandedLocations[locationName];

                      return (
                        <div key={locationName} style={{ marginBottom: '15px' }}>
                          <h3
                            onClick={() => toggleLocationCollapse(locationName)}
                            style={{
                              backgroundColor: isDarkMode ? '#2c2c2c' : '#e9ecef',
                              padding: '12px 15px',
                              borderRadius: '4px',
                              borderLeft: '4px solid #007bff',
                              marginTop: '0',
                              marginBottom: isExpanded ? '0' : '0',
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              userSelect: 'none'
                            }}
                          >
                            <span style={{ fontSize: '1.2rem' }}>Location: {locationName} <span style={{ fontSize: '0.9rem', color: '#888', marginLeft: '10px' }}>({parts.length} parts)</span></span>
                            <span>{isExpanded ? '▼' : '▶'}</span>
                          </h3>

                          {isExpanded && (
                            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px', marginBottom: '20px' }}>
                              <thead>
                                <tr style={{ borderBottom: `2px solid ${isDarkMode ? '#555' : '#ccc'}`, textAlign: 'left' }}>
                                  <th style={{ padding: '10px' }}>Nomenclature</th>
                                  <th style={{ padding: '10px' }}>Buno</th>
                                  <th style={{ padding: '10px' }}>Status</th>
                                  <th style={{ padding: '10px', textAlign: 'right' }}>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {parts.map(part => (
                                  <tr key={part.id} style={{ borderBottom: `1px solid ${isDarkMode ? '#333' : '#eee'}` }}>
                                    <td style={{ padding: '10px' }}>{part.nomenclature}</td>
                                    <td style={{ padding: '10px' }}>{part.tail_number}</td>
                                    <td style={{ padding: '10px' }}>
                                      <select
                                        value={part.status}
                                        onChange={(e) => handleInventoryStatusChange(part.id, e.target.value)}
                                        style={{
                                          padding: '6px',
                                          borderRadius: '4px',
                                          border: `1px solid ${isDarkMode ? '#555' : '#ccc'}`,
                                          backgroundColor: isDarkMode ? '#333' : '#fff',
                                          color: isDarkMode ? '#fff' : '#000',
                                          fontSize: '0.85rem',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        <option value="AWAITING_MAINTENANCE">Awaiting Maintenance</option>
                                        <option value="IN_WORK">In Work</option>
                                        <option value="SENT_TO_SUPPLY">Sent to Supply</option>
                                        <option value="READY_FOR_ISSUE">Ready for Issue</option>
                                        <option value="SCRAPPED">Scrapped</option>
                                      </select>
                                    </td>
                                    <td style={{ padding: '10px', textAlign: 'right', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                      <button
                                        onClick={() => {
                                          setGeneratedPart(part);
                                          setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }), 100);
                                        }}
                                        style={{ padding: '6px 12px', backgroundColor: '#007bff', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.9rem' }}
                                      >
                                        Barcode
                                      </button>
                                      <button
                                        onClick={() => handleDeletePart(part.id)}
                                        style={{ padding: '6px 12px', backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.9rem' }}
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      );
                    })
                )}
              </>
            )}
          </div>
        )}

        {/* Generated Barcode Display */}
        {(activeTab === 'assign' || activeTab === 'inventory') && generatedPart && (
          <div id="printable-barcode" style={{ marginTop: '20px', padding: '20px', border: '2px dashed #ccc', textAlign: 'center', backgroundColor: '#fff', color: '#000' }}>
            <h3 style={{ margin: '0 0 10px 0' }}>{generatedPart.nomenclature}</h3>
            <p style={{ margin: '0 0 10px 0' }}>Tail: {generatedPart.tail_number}</p>
            <p style={{ margin: '0 0 10px 0' }}>Loc: {generatedPart.location}</p>
            <BarcodeDisplay value={generatedPart.barcode_id} />
            <div className="no-print" style={{ marginTop: '15px' }}>
              <button onClick={handlePrint} style={{ padding: '10px 20px', backgroundColor: '#007bff', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Print Label</button>
            </div>
          </div>
        )}

      </div>

      <style>
        {`
          @media print {
            body { background-color: #fff; }
            .no-print { display: none !important; }
            #printable-barcode { border: none !important; margin: 0 !important; padding: 0 !important; width: 100% !important; }
          }
        `}
      </style>
    </div>
  );
};

export default PartTracker;
